import AppContext from "PFApp/app_context";
import { usePage } from "PFApp/use_page";
import { useIsShortlistingPermitted } from "PFCore/helpers/shortlists";
import { useTemplate } from "PFCore/helpers/templates";
import { useActivityParticipants } from "PFCore/hooks/queries/activity/use_activity_participants";
import { LocationState } from "PFTypes/router";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

const ACTIVITIES_PATH = "activities";

const checkRole = (key: string) => ["role", "audit_role"].includes(key);
const checkEngagement = (key: string) => ["engagement", "audit_engagement"].includes(key);

export type ActivityPageTab = {
  id: string;
  displayElement: string;
};

type ActivityPage = {
  tab?: ActivityPageTab;
  tabs?: ActivityPageTab[];
};

type UseActivityPageTabsReturn = {
  showTabs: boolean;
  tabs: ActivityPageTab[];
  tabActiveId: string | undefined;
  tabsIds: string[];
  handleChangeTab: (tab: ActivityPageTab["id"], navigate?: boolean) => void;
};

const useActivityPageTabs = (task, tabId = "details"): UseActivityPageTabsReturn => {
  const { t } = useTranslation("activities", { keyPrefix: "show.tabs" });
  const history = useHistory<LocationState>();
  const location = useLocation<LocationState>();
  const {
    page: { tab, tabs = [] },
    mergePage,
    resetPage
  } = usePage<ActivityPage>(`activities_${task.id}`);

  const {
    store: { currentProfile }
  } = useContext(AppContext);

  /* @ts-ignore */
  const template = useTemplate(task.template_id);
  const { data: participants } = useActivityParticipants(task.id);
  const isRole = checkRole(template?.key || "");
  const isEngagement = checkEngagement(template?.key || "");
  const needMatches = template?.need_matches;
  // eslint-disable-next-line camelcase
  const isReviewer = task.reviewers?.find(({ profile_id }) => profile_id === currentProfile.id);

  const ownGroup = currentProfile.permissions_group;
  const isResourcer = ownGroup?.permissions.find(({ name, permitted }) => name === "resourcer" && permitted);
  const isShortlistingPermitted = useIsShortlistingPermitted();

  const canSeeMatches = needMatches && task.coowned && (!isRole || isResourcer);
  const canSeeShortlist = (isShortlistingPermitted(task) || isReviewer) && isRole;
  const canSeeVacancies = task.coowned && isRole;
  const canSeeRoles = task.coowned && isEngagement;
  const canSeeInterested = task.coowned && isRole;
  const canSeeFeedback = task.coowned && isEngagement && participants && participants?.meta.total > 0;

  const tabValues: Record<string, ActivityPageTab> = useMemo(
    () => ({
      matches: {
        id: "matches",
        displayElement: t("matches")
      },
      shortlist: {
        id: "shortlist",
        displayElement: t("shortlist")
      },
      roles: {
        id: "roles",
        displayElement: t("roles")
      },
      details: {
        id: "details",
        displayElement: t("details")
      },
      interested: {
        id: "interested",
        displayElement: t("interested")
      },
      vacancies: {
        id: "vacancies",
        displayElement: t("vacancies")
      },
      feedback: {
        id: "feedback",
        displayElement: t("feedback")
      }
    }),
    [t]
  );

  useEffect(() => {
    const tabs: ActivityPageTab[] = [tabValues.details];

    if (canSeeInterested) {
      tabs.push(tabValues.interested);
    }

    if (canSeeMatches) {
      tabs.push(tabValues.matches);
    }

    if (canSeeShortlist) {
      tabs.push(tabValues.shortlist);
    }

    if (canSeeRoles) {
      tabs.push(tabValues.roles);
    }

    if (canSeeVacancies) {
      tabs.push(tabValues.vacancies);
    }

    if (canSeeFeedback) {
      tabs.push(tabValues.feedback);
    }

    mergePage({ tabs, tab: tabs.filter((tab) => tab.id === tabId)[0] || tabs[0] });
    return resetPage;
  }, [canSeeMatches, canSeeShortlist, canSeeRoles, canSeeVacancies, canSeeFeedback]);

  const handleChangeTab = (tabId: ActivityPageTab["id"], navigate = true) => {
    const utmSource = location?.state?.utmSource;

    mergePage({ tab: tabs.find(({ id }) => id === tabId) });

    if (navigate) {
      if (tabId === "details") {
        history.push({ pathname: `/${ACTIVITIES_PATH}/${task.id}`, state: { utmSource } });
      } else {
        history.push({ pathname: `/${ACTIVITIES_PATH}/${task.id}/${tabId}`, state: { utmSource } });
      }
    }
  };

  const showTabs = useMemo(() => tabs && tabs.length > 1, [tabs]);
  const tabsIds = useMemo(() => (tabs ? tabs.map((tab) => tab.id) : []), [tabs]);

  // Handle back button
  useEffect(() => {
    if (tab && tabId !== tab.id) {
      handleChangeTab(tabId as ActivityPageTab["id"], false);
    }
  }, [tabId]);

  return {
    showTabs,
    tabs,
    tabActiveId: tab && tab.id,
    tabsIds,
    handleChangeTab
  };
};

export default useActivityPageTabs;
